import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { MerchantGuard } from './guards/RouteGuard';
import { Layout } from './components/shared/Layout';
import { LanguageProvider } from './i18n';
import { useEffect } from 'react';
import { useTranslation } from './i18n';
import { setToastTranslationFunction } from './utils/i18n-toast';

// Import auth components
import MerchantUsernameLogin from './pages/auth/merchant/MerchantUsernameLogin';

// Landing page
import LandingPage from './pages/landing/LandingPage';
import ContactPage from './pages/landing/ContactPage';
import PrivacyPage from './pages/landing/PrivacyPage';

// Story 2-8: Import AuthProvider for Supabase auth integration
import { AuthProvider } from './features/auth/context/AuthProvider';

// User page components kept but not routed (customers use mobile app)

// Merchant pages - Updated with new 4-page architecture
import MerchantDashboard from './pages/merchant/Dashboard';
import CouponCreationV2 from './pages/merchant/CouponCreationV2';
import Coupons from './pages/merchant/Coupons';
import CouponDetail from './pages/merchant/CouponDetail';
import MemberList from './pages/merchant/MemberList';
import Redemption from './pages/merchant/Redemption';
import RedemptionHistory from './pages/merchant/RedemptionHistory';
import TransactionHistory from './pages/merchant/TransactionHistory';
import MerchantSettings from './pages/merchant/Settings';

// Create a QueryClient instance
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
		},
		mutations: {
			retry: false,
		},
	},
});

// Component to initialize i18n toast
function AppContent() {
	const { t } = useTranslation();

	useEffect(() => {
		setToastTranslationFunction(t);
	}, [t]);

	return (
		<Router>
			<Toaster position='top-right' />
			<Routes>
				{/* Public pages */}
				<Route path='/' element={<LandingPage />} />
				<Route path='/contact' element={<ContactPage />} />
				<Route path='/privacy' element={<PrivacyPage />} />

				{/* Public Routes - Authentication (outside Layout) */}
				{/* Merchant Authentication Routes */}
				<Route path='/merchant/login' element={<MerchantUsernameLogin />} />

				{/* Protected Routes (inside Layout) */}
				<Route
					path='/*'
					element={
						<Layout>
							<Routes>
								{/* Merchant Routes - Updated with new 4-page architecture */}
								<Route
									path='/merchant/*'
									element={
										<MerchantGuard>
											<Routes>
												{/* Main 4-page merchant admin interface */}
												<Route path='dashboard' element={<MerchantDashboard />} />
												<Route path='coupon-list' element={<Coupons />} />
												<Route path='coupon-list/new' element={<CouponCreationV2 />} />
												<Route path='coupon-list/edit/:id' element={<CouponCreationV2 />} />
												<Route path='coupons/:couponId' element={<CouponDetail />} />
												<Route path='member-list' element={<MemberList />} />
												<Route path='redemption' element={<Redemption />} />
												<Route path='redemption-history' element={<RedemptionHistory />} />
												<Route path='transactions' element={<TransactionHistory />} />
												<Route path='settings' element={<MerchantSettings />} />

												<Route
													path='*'
													element={<Navigate to='/merchant/dashboard' replace />}
												/>
											</Routes>
										</MerchantGuard>
									}
								/>

								{/* Fallback for unknown protected routes */}
								{/* Story 2-8: Redirect unknown routes to merchant login (not user login) */}
								<Route path='*' element={<Navigate to='/merchant/login' replace />} />
							</Routes>
						</Layout>
					}
				/>
			</Routes>
		</Router>
	);
}

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			{/* Story 2-8: AuthProvider wraps app for Supabase auth context (AC #2) */}
			<AuthProvider>
				<LanguageProvider>
					<AppContent />
				</LanguageProvider>
			</AuthProvider>
		</QueryClientProvider>
	);
}

export default App;
